import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Printer, Copy, Check, MessageCircle } from 'lucide-react';
import * as tienda from '@/api/tienda.api';
import { textoDelComprobante } from './texto-del-comprobante';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { useToast } from '@/hooks/useToast';
import { useWorkspaceStore } from '@/store/workspace.store';
import { dinero, fecha } from '@/lib/formato-tienda';

/**
 * EL COMPROBANTE DE LA VENTA.
 *
 * ═══ DE DÓNDE SALE ═══
 *
 * En la reunión: *«El lunes tú tienes tu sistema, por lo menos en la parte de
 * inventario y facturación»*. El inventario se construyó; esto es la otra
 * mitad — lo que la clienta recibe y lo que el mensajero lleva en la mano.
 *
 * ═══ NO ES UNA FACTURA FISCAL, Y ESO SE DICE ═══
 *
 * No lleva NCF ni entra en el 607. De comprobantes fiscales no se habló en la
 * reunión, y un papel que se PARECE a una factura sin serlo es peor que no
 * tener nada: alguien lo va a usar para declarar. Por eso el pie lo dice.
 *
 * ═══ POR QUÉ NO LO MANDA SOLO ═══
 *
 * El botón de WhatsApp abre el chat con el texto escrito y la persona pulsa
 * enviar. El sistema no manda mensajes a clientas por su cuenta: un
 * comprobante que sale solo de una venta que todavía se estaba corrigiendo es
 * un mensaje que no se puede recoger.
 */

export interface ComprobanteProps {
  /**
   * El ID, no la venta.
   *
   * Recibía el objeto, y quien lo abría desde la tabla le pasaba la fila de la
   * LISTA — que no trae el precio de cada línea. El comprobante enseñaba
   * «RD$NaN» en los renglones y el total bien, que es la peor combinación
   * posible: parece un detalle y es un papel que se le da a una clienta.
   *
   * Pidiendo la venta completa, además, el papel refleja lo que hay ahora y no
   * lo que había cuando se dibujó la tabla.
   */
  ventaId: string;
  onCerrar: () => void;
}

export function Comprobante({ ventaId, onCerrar }: ComprobanteProps) {
  const toast = useToast();
  const [copiado, setCopiado] = useState(false);
  const negocio = useWorkspaceStore(
    (s) => s.items.find((w) => w.id === s.activeId)?.name ?? 'Tu tienda',
  );

  const { data: venta, isLoading } = useQuery({
    queryKey: ['venta', ventaId],
    queryFn: () => tienda.leerVenta(ventaId),
  });

  const texto = useMemo(
    () => (venta ? textoDelComprobante(venta, negocio) : ''),
    [venta, negocio],
  );
  const piezas = venta?.lineas.reduce((a, l) => a + l.cantidad, 0) ?? 0;
  const bruto =
    venta?.lineas.reduce((a, l) => a + l.unitPrice * l.cantidad - l.descuento, 0) ?? 0;

  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(texto);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    } catch {
      // El portapapeles falla sin https o sin permiso. Se dice, en vez de que
      // parezca que copió: pegar un texto viejo en un WhatsApp es peor.
      toast.error('El navegador no dejó copiar. Selecciona el texto a mano.');
    }
  };

  const porWhatsApp = () => {
    const tel = (venta?.cliente?.telefono ?? '').replace(/\D/g, '');
    // Sin número no hay a quién abrirle el chat; se copia y ya.
    if (!tel) {
      void copiar();
      toast.success('Copiado. Esta clienta no tiene WhatsApp guardado.');
      return;
    }
    window.open(`https://wa.me/${tel}?text=${encodeURIComponent(texto)}`, '_blank', 'noopener');
  };

  if (isLoading || !venta) {
    return (
      <Modal open onClose={onCerrar} title="Comprobante">
        <p className="py-8 text-center text-sm text-slate-500">
          {isLoading ? 'Cargando la venta…' : 'No se encontró la venta.'}
        </p>
      </Modal>
    );
  }

  return (
    <Modal open onClose={onCerrar} title={`Comprobante ${venta.numero}`}>
      <div className="space-y-4">
        {/* Lo que se imprime. `id` para que el CSS de impresión lo aísle. */}
        <div
          id="comprobante-para-imprimir"
          className="rounded-lg border border-surface-border bg-surface p-5"
        >
          <div className="text-center">
            <p className="text-lg font-semibold text-slate-900 dark:text-slate-100">{negocio}</p>
            <p className="text-xs text-slate-500">
              {venta.numero} · {fecha(venta.createdAt, true)}
            </p>
          </div>

          {venta.cliente && (
            <p className="mt-3 text-sm text-slate-700 dark:text-slate-200">
              <span className="text-slate-500">Para:</span> {venta.cliente.nombre}
              {venta.cliente.telefono && (
                <span className="text-slate-500"> · {venta.cliente.telefono}</span>
              )}
            </p>
          )}

          <table className="mt-4 w-full text-sm">
            <tbody className="divide-y divide-surface-border">
              {venta.lineas.map((l) => (
                <tr key={l.id}>
                  <td className="py-1.5 pr-2 align-top tabular-nums text-slate-500">
                    {l.cantidad}×
                  </td>
                  <td className="py-1.5 pr-2">
                    <span className="text-slate-900 dark:text-slate-100">
                      {l.productVariant.product.sku && (
                        <span className="font-mono text-xs text-slate-500">
                          {l.productVariant.product.sku}{' '}
                        </span>
                      )}
                      {l.productVariant.product.name}
                    </span>
                    <span className="text-slate-500"> · {l.productVariant.label}</span>
                  </td>
                  <td className="py-1.5 text-right tabular-nums text-slate-900 dark:text-slate-100">
                    {dinero(l.unitPrice * l.cantidad - l.descuento)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="border-t-2 border-surface-border">
              {venta.descuento > 0 && (
                <>
                  <tr>
                    <td colSpan={2} className="pt-2 text-slate-500">
                      Subtotal
                    </td>
                    <td className="pt-2 text-right tabular-nums">{dinero(bruto)}</td>
                  </tr>
                  <tr>
                    <td colSpan={2} className="text-slate-500">
                      Descuento
                    </td>
                    <td className="text-right tabular-nums text-amber-700 dark:text-amber-500">
                      −{dinero(venta.descuento)}
                    </td>
                  </tr>
                </>
              )}
              {venta.envio > 0 && (
                <tr>
                  <td colSpan={2} className="text-slate-500">
                    Envío
                  </td>
                  <td className="text-right tabular-nums">{dinero(venta.envio)}</td>
                </tr>
              )}
              <tr>
                <td colSpan={2} className="pt-1 font-semibold text-slate-900 dark:text-slate-100">
                  TOTAL
                </td>
                <td className="pt-1 text-right text-base font-semibold tabular-nums text-slate-900 dark:text-slate-100">
                  {dinero(venta.total)}
                </td>
              </tr>
            </tfoot>
          </table>

          <p className="mt-3 text-xs text-slate-500">
            {piezas} pieza{piezas === 1 ? '' : 's'}
            {venta.formaDePago && ` · ${venta.formaDePago}`}
            {venta.estado === 'CONFIRMADA' && !venta.cobrado && ' · pendiente de cobro al entregar'}
          </p>
          {venta.direccion && (
            <p className="mt-1 text-xs text-slate-500">Entrega: {venta.direccion}</p>
          )}

          {/* Sin esto, alguien lo va a usar para declarar. */}
          <p className="mt-4 border-t border-surface-border pt-2 text-center text-[11px] text-slate-400">
            Comprobante de entrega. No es una factura con valor fiscal (sin NCF).
          </p>
        </div>

        <div className="flex flex-wrap justify-end gap-2">
          <Button variant="ghost" onClick={onCerrar}>
            Cerrar
          </Button>
          <Button variant="outline" onClick={() => void copiar()}>
            {copiado ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            {copiado ? 'Copiado' : 'Copiar el texto'}
          </Button>
          <Button variant="outline" onClick={() => window.print()}>
            <Printer className="h-4 w-4" />
            Imprimir
          </Button>
          <Button onClick={porWhatsApp}>
            <MessageCircle className="h-4 w-4" />
            Mandar por WhatsApp
          </Button>
        </div>
        <p className="text-right text-xs text-slate-500">
          WhatsApp se abre con el texto escrito. Lo mandas tú.
        </p>
      </div>
    </Modal>
  );
}
